// `[[` in a body: a list of the person's documents; choosing one inserts a
// reference to it (`[[page:<id>|<title>]]`, the `trameriaLink` node), drawn as
// a chip with its current title.
//
// Promise:
// - Opens anywhere in text (after any character: references are written
//   mid-sentence, and Japanese has no spaces).
// - With nothing typed after `[[` the latest changed documents are listed; with
//   a query, the documents whose title or text has it, titles first. A failed
//   request lists nothing (the list never breaks typing) and is asked again on
//   the next keystroke.
// - ↑ ↓ move, Enter or Tab choose, Escape closes. The chosen reference is
//   followed by a space, so typing goes on after the chip.
import type { QueryClient } from "@tanstack/react-query";
import { Extension } from "@tiptap/core";
import { ReactRenderer } from "@tiptap/react";
import Suggestion, { type SuggestionOptions } from "@tiptap/suggestion";
import { m } from "@trameria/i18n";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import type { Api } from "../../data/api";
import { Icon } from "../../ui/icon";
import { isImeComposing } from "../../ui/ime";
import { type LinkCandidate, linkCandidatesQuery } from "../queries";
import {
  POPUP_PANEL,
  POPUP_ROW,
  raisePopup,
  SuggestEmpty,
  suggestKeyDown,
} from "./suggest";

const MAX_ITEMS = 20;

export interface LinkSuggestContext {
  api: Api;
  qc: QueryClient;
  /** The document being written: not offered (a link to itself goes nowhere). */
  currentId?(): string | null;
}

export async function linkCandidates(
  context: LinkSuggestContext | null,
  query: string,
): Promise<LinkCandidate[]> {
  if (!context) return [];
  try {
    const list = await context.qc.fetchQuery(
      linkCandidatesQuery(context.api, query),
    );
    const self = context.currentId?.() ?? null;
    return list.filter((c) => c.id !== self).slice(0, MAX_ITEMS);
  } catch {
    return [];
  }
}

interface ListProps {
  items: LinkCandidate[];
  command(item: LinkCandidate): void;
}

export interface LinkSuggestHandle {
  onKeyDown(props: { event: KeyboardEvent }): boolean;
}

export const LinkSuggestList = forwardRef<LinkSuggestHandle, ListProps>(
  function LinkSuggestList({ items, command }, ref) {
    const [selected, setSelected] = useState(0);
    // frontend-development: not a fetch; a new query starts at the first item.
    // biome-ignore lint/correctness/useExhaustiveDependencies: the change of items is the trigger
    useEffect(() => {
      setSelected(0);
    }, [items]);
    useImperativeHandle(
      ref,
      () => ({
        onKeyDown({ event }) {
          if (isImeComposing(event)) return false;
          return suggestKeyDown(event, {
            items,
            selected,
            setSelected,
            command,
            confirmKeys: ["Enter", "Tab"],
          });
        },
      }),
      [items, selected, command],
    );
    return (
      <div
        className={`${POPUP_PANEL} max-h-72 w-72`}
        role="listbox"
        aria-label={m.documents_link_suggest()}
      >
        {items.length === 0 ? (
          <SuggestEmpty />
        ) : (
          items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              role="option"
              aria-selected={index === selected}
              tabIndex={-1}
              onMouseDown={(e) => {
                e.preventDefault();
                command(item);
              }}
              onMouseEnter={() => setSelected(index)}
              className={`${POPUP_ROW} text-body ${index === selected ? "bg-hover" : ""}`}
            >
              <Icon kind="documents" size={14} className="text-muted" />
              <span className="pointer-events-none min-w-0 flex-1 truncate">
                {item.title || m.documents_untitled()}
              </span>
            </button>
          ))
        )}
      </div>
    );
  },
);

export interface LinkSuggestOptions {
  /** Where candidates come from (outside React, so given when the editor is made). */
  context: LinkSuggestContext | null;
  suggestion: Omit<SuggestionOptions<LinkCandidate>, "editor" | "items">;
}

export const LinkSuggest = Extension.create<LinkSuggestOptions>({
  name: "trameriaLinkSuggest",

  addOptions() {
    return {
      context: null,
      suggestion: {
        char: "[[",
        allowedPrefixes: null,
        // The default plugin key: the `/` menu has its own.
        command: ({ editor, range, props }) => {
          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: "trameriaLink",
                attrs: { kind: "page", refId: props.id, label: props.title },
              },
              { type: "text", text: " " },
            ])
            .run();
        },
        render: () => {
          let component: ReactRenderer<LinkSuggestHandle, ListProps> | null =
            null;
          let unmount: (() => void) | null = null;
          return {
            onStart: (props) => {
              component = new ReactRenderer(LinkSuggestList, {
                props: { items: props.items, command: props.command },
                editor: props.editor,
              });
              unmount = props.mount(raisePopup(component.element));
            },
            onUpdate: (props) =>
              component?.updateProps({
                items: props.items,
                command: props.command,
              }),
            onKeyDown: (props) => component?.ref?.onKeyDown(props) ?? false,
            onExit: () => {
              unmount?.();
              unmount = null;
              component?.destroy();
              component = null;
            },
          };
        },
      },
    };
  },

  addProseMirrorPlugins() {
    const context = this.options.context;
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
        items: ({ query }) => linkCandidates(context, query),
      }),
    ];
  },
});
